import React, { useContext, useState, useEffect, useRef } from "react";
import { useSpeakingDetector } from "../../hooks/useSpeakingDetector";
import { ThemeContext } from "../../contexts/ThemeContext";
import ParticipantAudio from "./ParticipantAudio";
import MuteComponent from "./MuteComponent";
import DegradedBadge from "./DegradedBadge";
import gsap from "gsap";
import { Draggable } from "gsap/Draggable";

gsap.registerPlugin(Draggable);

export default function MeetingWhatsApp({
  users,
  mediasTools,
  activeSpeakerId,
}) {
  const { localStream, degradedUserIds } = mediasTools;
  const { theme } = useContext(ThemeContext);
  const [color, setColor] = useState(theme === "dark" ? "#fff" : "#023377");
  const isSpeaking = useSpeakingDetector(localStream, false);
  const [imageErrors, setImageErrors] = useState({});

  const containerRef = useRef(null);
  const localRef = useRef(null);
  const dragInstance = useRef(null);

  const handleImageError = (userId) =>
    setImageErrors((prev) => ({ ...prev, [userId]: true }));

  useEffect(() => {
    setColor(theme === "dark" ? "#fff" : "#023377");
  }, [theme]);

  // Draggable sur l'overlay local
  useEffect(() => {
    if (!localRef.current || !containerRef.current) return;

    dragInstance.current = Draggable.create(localRef.current, {
      type: "x,y",
      bounds: containerRef.current,
      inertia: false,
      cursor: "grab",
      activeCursor: "grabbing",
      edgeResistance: 0.5,
      zIndexBoost: false,
    })[0];

    return () => {
      if (dragInstance.current) dragInstance.current.kill();
    };
  }, [users.length]);

  const local = users.find((u) => u.isLocal);
  const remote = users.find((u) => !u.isLocal) || users[0];

  const nameOverlayStyle = {
    position: "absolute",
    bottom: "12px",
    left: "19px",
    zIndex: 2,
    color: "#fff",
    fontWeight: 600,
    backgroundColor: "rgba(0,0,0,0.5)",
    padding: "2px 8px",
    borderRadius: "6px",
    pointerEvents: "none",
    maxWidth: "80%",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  };

  const renderParticipant = (participant, isMain) => {
    const track = participant.stream?.getVideoTracks?.()[0];
    const speaking =
      (participant.isLocal && isSpeaking) ||
      (!participant.isLocal && activeSpeakerId === participant.userId);

    if (participant.videoOn && track) {
      return (
        <>
          <video
            ref={(ref) => {
              if (ref && ref.srcObject !== participant.stream) {
                ref.srcObject = participant.stream;
              }
            }}
            autoPlay
            muted={participant.isLocal}
            playsInline
            style={{
              transform: participant.isLocal ? "scaleX(-1)" : "none",
              objectFit: "cover",
              width: "100%",
              height: "100%",
              borderRadius: isMain ? "10px" : "12px",
            }}
          />
          <ParticipantAudio participant={participant} />
          <span
            style={{
              ...nameOverlayStyle,
              fontSize: isMain ? "clamp(12px, 1.4vw, 15px)" : "10px",
            }}
          >
            {participant.displayName}
          </span>
        </>
      );
    }

    return (
      <div className="video-off-blur" style={{ height: "100%" }}>
        <ParticipantAudio participant={participant} />
        <img
          src={participant.userImage}
          alt="Background"
          className="blur-bg"
          onError={() => handleImageError(participant.userId)}
        />
        <div className={`avatar-wrapper ${speaking ? "speaking" : ""}`}>
          {participant.userImage && !imageErrors[participant.userId] ? (
            <img
              src={participant.userImage}
              alt="User"
              className={`user-avatar ${
                isMain ? "" : "user-avatar-whatsapp-local"
              }`}
              onError={() => handleImageError(participant.userId)}
            />
          ) : (
            <div
              className={`user-avatar ${
                isMain ? "" : "user-avatar-whatsapp-local"
              }`}
              style={{
                alignItems: "center",
                display: "flex",
                justifyContent: "center",
                fontSize: isMain
                  ? "clamp(28px, 8vw, 50px)"
                  : "clamp(14px, 3vw, 22px)",
                color,
              }}
            >
              {participant.displayName?.[0]}
              {participant.displayName?.split(" ")[1]?.charAt(0)}
            </div>
          )}
        </div>
        <span
          style={{
            ...nameOverlayStyle,
            fontSize: isMain ? "clamp(12px, 1.4vw, 15px)" : "10px",
          }}
        >
          {participant.displayName}
        </span>
      </div>
    );
  };

  return (
    <div className="meeting-whatsapp-container" ref={containerRef}>
      {/* Remote plein ecran */}
      <div className="meeting-whatsapp-remote" style={{ position: "relative" }}>
        {remote && (
          <>
            {!remote.audioOn && <MuteComponent />}
            {degradedUserIds?.includes(remote.userId) && <DegradedBadge />}
            {renderParticipant(remote, true)}
          </>
        )}
      </div>

      {/* Local overlay draggable */}
      {local && users.length > 1 && (
        <div
          ref={localRef}
          className="meeting-whatsapp-local"
          style={{ position: "absolute" }}
        >
          {!local.audioOn && <MuteComponent />}
          {degradedUserIds?.includes(local.userId) && (
            <DegradedBadge compact />
          )}
          {renderParticipant(local, false)}
        </div>
      )}
    </div>
  );
}
